import {isUndefined} from 'lodash';
import {createSelector} from 'reselect';

import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {
  areFiltersIncludingCounterVisibility,
  buildCounterContext,
  CounterContext,
  getCounter,
  isConversationMatchingCounterVisibility,
} from '../../helpers/countersMonitorHelpers';
import {AppSelector} from '../../helpers/reselectHelpers';
import {ConversationModel} from '../../models/api/conversationModel';
import {CountersVisibilityModesEnum} from '../../models/api/preferencesModel';
import {AsyncMoreStatusesEnum} from '../appTypes';
import {CellsPanelConversationsPageState, CellsPanelPageTypesEnum} from '../panels/cells/cellsPanelState';
import {
  InboxesConversationsParameters,
  InboxesParametersTypesEnum,
} from '../panels/inboxes/parameters/inboxesParameters';
import {getCellsPanel} from '../panels/panelsSelectors';
import {getConversations, getCounters} from './dataSelectors';
import {getCustomViews} from './preferences/customViewsSelectors';
import {getCountersVisibilities, getShouldUseOpenFilterForTeams} from './preferences/preferencesSelectors';
import {getUserId, getUserNamespace} from './teammates/teammatesSelectors';

/*
 * Constants.
 */

const bigCounterLimit = 40;

/*
 * Selectors.
 */

const getSelectedConversationCellsPage: AppSelector<CellsPanelConversationsPageState | undefined> =
  createSelector([getCellsPanel], (cellsPanel) => {
    if (!cellsPanel) {
      return undefined;
    }

    // Only count conversations.
    const page = cellsPanel.pages.current;
    if (!page || page.type !== CellsPanelPageTypesEnum.CONVERSATIONS) {
      return undefined;
    }

    // Ignore if not fully loaded.
    if (
      page.status !== AsyncStatusesEnum.LOADED ||
      page.moreStatusTop !== AsyncMoreStatusesEnum.ALL_LOADED ||
      page.moreStatusBottom !== AsyncMoreStatusesEnum.ALL_LOADED
    ) {
      return undefined;
    }

    // Ignore if page is too big.
    if (page.conversationIds.length > bigCounterLimit) {
      return undefined;
    }

    return page;
  });

const getSelectedConversationCellsParams = createSelector(
  [getSelectedConversationCellsPage],
  (page) => page?.parameters,
);

type CellPageConfig = Pick<CellsPanelConversationsPageState, 'cellsContext' | 'parameters'>;
export type GetPageConfig = AppSelector<CellPageConfig | undefined>;

export const makeGetPageCounterVisibility = (getPage: GetPageConfig) =>
  createSelector(
    [getPage, getUserId, getUserNamespace, getCountersVisibilities, getShouldUseOpenFilterForTeams],
    (page, userId, userNamespace, countersVisibilities, shouldShowTeammateTabsInTeamContext) => {
      if (!page) {
        return undefined;
      }

      const {cellsContext, parameters} = page;

      // Do not check counters in delegations.
      if (cellsContext.teammateId !== userId) {
        return undefined;
      }

      switch (parameters.type) {
        case InboxesParametersTypesEnum.USER:
        case InboxesParametersTypesEnum.TEAMMATE_INBOX:
          return countersVisibilities[userNamespace] || CountersVisibilityModesEnum.OPEN;

        case InboxesParametersTypesEnum.INBOX:
          return shouldShowTeammateTabsInTeamContext
            ? CountersVisibilityModesEnum.OPEN
            : CountersVisibilityModesEnum.UNASSIGNED;

        case InboxesParametersTypesEnum.TEAMMATE:
        case InboxesParametersTypesEnum.TEAMMATE_VIEW:
        case InboxesParametersTypesEnum.VIEW:
        case InboxesParametersTypesEnum.GLOBAL:
          return CountersVisibilityModesEnum.OPEN;

        default:
          return undefined;
      }
    },
  );

const getSelectedCounterVisibility = makeGetPageCounterVisibility(getSelectedConversationCellsPage);

const getConversationCellsIds = createSelector(
  [getSelectedConversationCellsPage, getSelectedCounterVisibility, getConversations],
  (page, counterVisibility, conversations) => {
    if (!page || !counterVisibility) {
      return undefined;
    }

    const {conversationIds, parameters, cellsContext} = page;
    if (!areFiltersIncludingCounterVisibility(parameters.filters, counterVisibility)) {
      return undefined;
    }

    return conversationIds
      .map((id) => conversations[id])
      .filter(
        (conversation): conversation is ConversationModel =>
          !isUndefined(conversation) &&
          isConversationMatchingCounterVisibility(conversation, counterVisibility, cellsContext),
      )
      .map((conversation) => conversation.id);
  },
);

type GetPageParams = AppSelector<InboxesConversationsParameters | undefined>;
type GetCounterVisibility = AppSelector<CountersVisibilityModesEnum | undefined>;

export function makeGetCounter(getPageParams: GetPageParams, getCounterVisibility: GetCounterVisibility) {
  return createSelector(
    [getPageParams, getCounterVisibility, getCounters, getCustomViews],
    (parameters, counterVisibility, counters, customViews) => {
      if (!parameters || !counterVisibility) {
        return undefined;
      }

      return getCounter(counters, counterVisibility, parameters, customViews);
    },
  );
}

const getSelectedCounter = makeGetCounter(getSelectedConversationCellsParams, getSelectedCounterVisibility);

export interface CounterCheck extends CounterContext {
  visibility: CountersVisibilityModesEnum | undefined;
  conversationIds: ReadonlyArray<number>;
  counter: number;
  ground: number;
  key: string;
}

export const getSelectedCounterCheck = createSelector(
  [
    getSelectedCounter,
    getSelectedCounterVisibility,
    getSelectedConversationCellsPage,
    getConversationCellsIds,
  ],
  (counter, visibility, page, conversationIds): CounterCheck | undefined => {
    if (!counter || !page || !conversationIds) {
      return undefined;
    }

    const counterContext = buildCounterContext(page.parameters, counter.key);
    if (!counterContext) {
      return undefined;
    }

    return {
      ...counterContext,
      visibility,
      counter: counter.value,
      ground: conversationIds.length,
      key: counter.key,
      conversationIds,
    };
  },
);
